/**
 * Active Alerts presenter. Groups persisted OPEN incidents by canonical
 * operational scope (condition family + camera + zone + alert class) — the
 * anti-storm key prefix without person subject. Does not invent incident
 * identity, Pattern counts, or qualified safety occurrence counts. Incident
 * row length is not an occurrenceCount.
 */
import { canonicalConditionFamily } from '../continuousSafety/conditionLifecyclePresentation';
import {
  historyAlertClass,
  presentAlertHistoryRow,
  type AlertHistoryAlertClass,
  type AlertHistoryLookups,
  type AlertHistoryRow,
} from './alertHistoryPresentation';
import type { BuyerLiveItem } from './liveAlertsReconciliation';

export type ActiveAlertCard = {
  key: string;
  conditionLabel: string;
  conditionFamily: string;
  cameraAreaLabel: string;
  alertClass: AlertHistoryAlertClass;
  severity: string;
  status: string;
  lastActivityAt: string | null;
  lastActivityLabel: string;
  investigateHref: string;
  representativeIncidentId: string;
  incidentIds: string[];
  deliveryLabel: string;
  deliveryCopy: string;
  rows: AlertHistoryRow[];
};

function text(value: unknown): string {
  return String(value || '').trim();
}

function isOpenStatus(status: string): boolean {
  const normalized = text(status).toUpperCase();
  return normalized !== 'RESOLVED' && normalized !== 'CLOSED';
}

function recordedMs(row: AlertHistoryRow): number {
  const parsed = Date.parse(row.recordedAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Anti-storm operational scope without person subject_key. */
export function activeAlertOperationalScopeKey(item: BuyerLiveItem): string {
  const op = item?.operational || ({} as BuyerLiveItem['operational']);
  if (historyAlertClass(item) === 'Composite') {
    return `composite|${text(op.incident_id)}`;
  }
  const family = canonicalConditionFamily(op.condition_type || op.incident_type) || 'UNKNOWN';
  const camera = text(op.camera_id) || '-';
  const zone = text(op.zone_id || op.area_id) || '-';
  return `${family}|${camera}|${zone}|${historyAlertClass(item)}`;
}

export function groupActiveAlertsByOperationalScope(
  items: BuyerLiveItem[],
  lookups: AlertHistoryLookups = {},
): ActiveAlertCard[] {
  const buckets = new Map<string, AlertHistoryRow[]>();
  for (const item of items || []) {
    const row = presentAlertHistoryRow(item, lookups);
    if (!row || !isOpenStatus(row.status)) continue;
    const key = activeAlertOperationalScopeKey(item);
    const members = buckets.get(key) || [];
    members.push(row);
    buckets.set(key, members);
  }
  const cards: ActiveAlertCard[] = [];
  buckets.forEach((members, key) => {
    const rows = members.slice().sort((a, b) => recordedMs(b) - recordedMs(a));
    const latest = rows[0];
    cards.push({
      key,
      conditionLabel: latest.conditionLabel,
      conditionFamily: latest.conditionFamily,
      cameraAreaLabel: latest.cameraAreaLabel,
      alertClass: latest.alertClass,
      severity: latest.severity,
      status: latest.status || 'OPEN',
      lastActivityAt: latest.recordedAt,
      lastActivityLabel: latest.timeLabel,
      investigateHref: latest.investigateHref,
      representativeIncidentId: latest.incidentId,
      incidentIds: rows.map((row) => row.incidentId),
      deliveryLabel: latest.deliveryLabel,
      deliveryCopy: latest.deliveryCopy,
      rows,
    });
  });
  return cards.sort((a, b) => {
    const byTime = Date.parse(b.lastActivityAt || '') - Date.parse(a.lastActivityAt || '');
    if (Number.isFinite(byTime) && byTime !== 0) return byTime;
    return a.key.localeCompare(b.key);
  });
}
